import { withUploadToast } from '../../lib/uploadToast';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatBytes } from '../../utils/format';
import {
  Check,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Folder,
  FolderInput,
  FolderPlus,
  Grid2x2,
  Grid3x3,
  History,
  Info,
  List,
  MessageSquare,
  MessagesSquare,
  MoreHorizontal,
  Pencil,
  Share2,
  SquarePen,
  Star,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import ContextMenu from '../../components/ui/ContextMenu';
import Spinner from '../../components/ui/Spinner';
import Breadcrumbs from './components/Breadcrumbs';
import ItemRow from './components/ItemRow';
import FileAvailability from './components/FileAvailability';
import ImageThumb, { isImageFile } from './components/ImageThumb';
import { useLocalPref } from '../../hooks/useLocalPref';
import NewFolderModal from './components/NewFolderModal';
import RenameModal from './components/RenameModal';
import ShareModal from './components/ShareModal';
import VersionHistoryModal from './components/VersionHistoryModal';
import MoveToFolderModal from './components/MoveToFolderModal';
import MetadataModal from './components/MetadataModal';
import UploadButton from './components/UploadButton';
import CommentsModal from '../comments/CommentsModal';
import ChatModal from '../chat/ChatModal';
import { chatCounts, chatKeys } from '../chat/chatApi';
import { getErrorMessage } from '../../lib/apiClient';
import { fileOpenPath, isEditableInEditor } from '../editor/editorApi';
import { commentCounts } from '../comments/commentsApi';
import { useTenant } from '../../hooks/useTenant';
import { ACTIONS } from '../tenancy/actions';
import {
  addFavorite,
  downloadFile,
  fileKeys,
  getContents,
  listFavorites,
  removeFavorite,
  trashFile,
  trashFolder,
  uploadFile,
} from './filesApi';

export default function FilesPage() {
  const { allows } = useTenant();
  const canWriteFiles = allows(ACTIONS.FILE_WRITE);
  const canManageFolders = allows(ACTIONS.FOLDER_MANAGE);
  const canCreateFiles = allows(ACTIONS.FILE_CREATE);
  const canCreateFolders = allows(ACTIONS.FOLDER_CREATE);
  const canDeleteFiles = allows(ACTIONS.FILE_DELETE);
  const canDownload = allows(ACTIONS.FILE_DOWNLOAD);
  const canShare = allows(ACTIONS.FILE_SHARE) && allows(ACTIONS.SHARE_CREATE);
  const canComment = allows(ACTIONS.COMMENT_WRITE);
  const canChat = allows(ACTIONS.CHAT_READ);
  const { folderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  // Version history hidden for now:
  // const [versionTarget, setVersionTarget] = useState(null);
  const [commentTarget, setCommentTarget] = useState(null);
  const [chatTarget, setChatTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [metaTarget, setMetaTarget] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [view, setView] = useLocalPref('mdu.layout', 'list'); // 'list' | 'grid' | 'large'
  const [tab, setTab] = useState('all'); // 'all' | 'recents' | 'starred'
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  // Right-click menu: { x, y, type, item } for an item, { x, y, page: true } for empty space.
  const [ctxMenu, setCtxMenu] = useState(null);
  const uploadInputRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [folderId]);

  useEffect(() => {
    if (!location.state?.openNewFolder) return;
    setNewFolderOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const params = { page, limit: 20, sortBy: 'name', sortOrder: 'ASC' };
  const { data, isLoading, isError, error } = useQuery({
    queryKey: fileKeys.contents(folderId, params),
    queryFn: () => getContents(folderId, params),
    placeholderData: keepPreviousData,
  });

  const folders = useMemo(() => data?.folders ?? [], [data]);
  const files = useMemo(() => data?.files?.data ?? [], [data]);
  const folderIds = folders.map((f) => f.id);
  const fileIds = files.map((f) => f.id);

  // Favorites set (drives the star toggles).
  const { data: favorites = [] } = useQuery({
    queryKey: fileKeys.favorites(),
    queryFn: listFavorites,
  });
  const favoriteSet = useMemo(
    () => new Set(favorites.map((f) => `${f.targetType}:${f.targetId}`)),
    [favorites],
  );
  const isFavorite = (type, id) => favoriteSet.has(`${type}:${id}`);

  // Per-row comment counts (one batched query per target type).
  const { data: folderCounts = {} } = useQuery({
    queryKey: ['comment-counts', 'folder', folderIds],
    queryFn: () => commentCounts('folder', folderIds),
    enabled: folderIds.length > 0,
  });
  const { data: fileCounts = {} } = useQuery({
    queryKey: ['comment-counts', 'file', fileIds],
    queryFn: () => commentCounts('file', fileIds),
    enabled: fileIds.length > 0,
  });

  // Per-row chat counts, batched the same way — the row badges are what tell
  // anyone a conversation is happening at all. Polled so a message sent while
  // this list is open (and the chat itself is closed) still raises a bubble.
  const { data: folderChat = {} } = useQuery({
    queryKey: chatKeys.counts('folder', folderIds),
    queryFn: () => chatCounts('folder', folderIds),
    enabled: canChat && folderIds.length > 0,
    refetchInterval: 15_000,
  });
  const { data: fileChat = {} } = useQuery({
    queryKey: chatKeys.counts('file', fileIds),
    queryFn: () => chatCounts('file', fileIds),
    enabled: canChat && fileIds.length > 0,
    refetchInterval: 15_000,
  });

  const trashMutation = useMutation({
    mutationFn: ({ type, item }) => (type === 'folder' ? trashFolder(item.id) : trashFile(item.id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('Moved to trash');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not move to trash')),
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ type, item }) =>
      isFavorite(type, item.id)
        ? removeFavorite({ targetType: type, targetId: item.id })
        : addFavorite({ targetType: type, targetId: item.id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fileKeys.favorites() }),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not update favorite')),
  });

  const handleDownload = async (file) => {
    const toastId = toast.loading(`Downloading ${file.name}…`);
    try {
      await downloadFile(file.id, file.name);
      toast.success('Download ready', { id: toastId });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Download failed'), { id: toastId });
    }
  };

  const uploadMany = async (dropped) => {
    if (!dropped.length) return;
    // One progress toast per file; a failed file doesn't stop the rest.
    for (const file of dropped) {
      await withUploadToast(file.name, (onProgress) =>
        uploadFile({ file, folderId, onProgress }),
      ).catch(() => {});
    }
    queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
    queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    if (!canCreateFiles) return;
    await uploadMany(Array.from(e.dataTransfer.files ?? []));
  };

  const meta = data?.files?.meta;
  const crumbs = data?.breadcrumbs ?? [];
  const title = crumbs.length ? crumbs[crumbs.length - 1].name : 'All files';
  const starredOnly = tab === 'starred';
  const visFolders = starredOnly ? folders.filter((f) => isFavorite('folder', f.id)) : folders;
  const visFiles = starredOnly ? files.filter((f) => isFavorite('file', f.id)) : files;
  const nothingToShow =
    !isLoading && !isError && visFolders.length === 0 && visFiles.length === 0;

  // Folders navigate directly. Office docs open straight into the ONLYOFFICE
  // editor (Drive-style — the viewer can't render them anyway); everything
  // else opens the full-screen viewer. `from` lets the editor close back here.
  const handleOpen = ({ type, item }) => {
    if (type === 'folder') {
      navigate(`/folder/${item.id}`);
    } else {
      navigate(fileOpenPath(item), { state: { from: location.pathname } });
    }
  };

  const handleEdit = (item) =>
    navigate(fileOpenPath(item), { state: { from: location.pathname } });

  /** Reading a thread advanced my cursor server-side; refetch so the badge drops. */
  const closeChat = () => {
    setChatTarget(null);
    queryClient.invalidateQueries({ queryKey: ['chat-counts'] });
  };

  const openItemMenu = (e, type, item) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, type, item });
  };
  const openPageMenu = (e) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, page: true });
  };

  /** Context-menu entries — thin wrappers over the existing handlers/modals. */
  const menuItems = (ctx) => {
    if (ctx.page) {
      return [
        canCreateFolders && { label: 'New folder', icon: FolderPlus, onClick: () => setNewFolderOpen(true) },
        canCreateFiles && { label: 'Upload files', icon: UploadCloud, onClick: () => uploadInputRef.current?.click() },
      ];
    }
    const { type, item } = ctx;
    const isFile = type === 'file';
    // For editable office docs "Open" IS the editor, so lead with an explicit
    // "Edit" instead of duplicating entries.
    const editable = isFile && isEditableInEditor(item);
    return [
      {
        label: editable ? 'Edit' : isFile ? 'Open' : 'Open folder',
        icon: editable ? SquarePen : ExternalLink,
        onClick: () => handleOpen({ type, item }),
      },
      isFile && canDownload && { label: 'Download', icon: Download, onClick: () => handleDownload(item) },
      { separator: true },
      ((isFile && canWriteFiles) || (!isFile && canManageFolders)) && { label: 'Rename', icon: Pencil, onClick: () => setRenameTarget({ type, item }) },
      ((isFile && canWriteFiles) || (!isFile && canManageFolders)) && { label: 'Move to…', icon: FolderInput, onClick: () => setMoveTarget({ type, item }) },
      canShare && { label: 'Share', icon: Share2, onClick: () => setShareTarget({ type, item }) },
      {
        label: isFavorite(type, item.id) ? 'Remove from starred' : 'Add to starred',
        icon: Star,
        onClick: () => favoriteMutation.mutate({ type, item }),
      },
      // Version history hidden for now:
      // isFile && { label: 'Version history', icon: History, onClick: () => setVersionTarget({ type, item }) },
      isFile && { label: 'Details', icon: Info, onClick: () => setMetaTarget({ type, item }) },
      canComment && { label: 'Comments', icon: MessageSquare, onClick: () => setCommentTarget({ type, item }) },
      canChat && { label: 'Chat', icon: MessagesSquare, onClick: () => setChatTarget({ type, item }) },
      { separator: true },
      ((isFile && canDeleteFiles) || (!isFile && canManageFolders)) && { label: 'Move to trash', icon: Trash2, danger: true, onClick: () => trashMutation.mutate({ type, item }) },
    ];
  };

  const LAYOUTS = [
    { value: 'grid', label: 'Grid', icon: Grid2x2 },
    { value: 'large', label: 'Large grid', icon: Grid3x3 },
    { value: 'list', label: 'List', icon: List },
  ];

  const Pill = ({ value, icon: Icon, children }) => (
    <button
      onClick={() => setTab((t) => (t === value ? 'all' : value))}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
        tab === value
          ? 'bg-slate-200 text-slate-900'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
      )}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );

  return (
    <div
      className="mx-auto flex h-full max-w-6xl flex-col"
      onDragOver={(e) => {
        if (!canCreateFiles) return;
        e.preventDefault();
        if (!dragging) setDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragging(false);
      }}
      onDrop={canCreateFiles ? handleDrop : undefined}
      onContextMenu={canCreateFiles || canCreateFolders ? openPageMenu : undefined}
    >
      {/* Content header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
        {(canCreateFolders || canCreateFiles) && (
          <div className="flex items-center gap-2">
            {canCreateFolders && <Button variant="secondary" onClick={() => setNewFolderOpen(true)}><FolderPlus className="h-4 w-4" />New folder</Button>}
            {canCreateFiles && <UploadButton folderId={folderId} />}
          </div>
        )}
      </div>

      {crumbs.length > 0 && (
        <div className="mt-2">
          <Breadcrumbs crumbs={crumbs} />
        </div>
      )}

      {/* Filter pills + view toggle */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pill value="recents" icon={Clock}>
            Recents
          </Pill>
          <Pill value="starred" icon={Star}>
            Starred
          </Pill>
        </div>
        <div className="relative">
          <button
            onClick={() => setLayoutMenuOpen((o) => !o)}
            aria-label="Change layout"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-100 px-2.5 py-2 text-slate-600 transition-colors hover:bg-slate-200"
          >
            {(() => {
              const Active = (LAYOUTS.find((l) => l.value === view) ?? LAYOUTS[2]).icon;
              return <Active className="h-4 w-4" />;
            })()}
          </button>
          {layoutMenuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setLayoutMenuOpen(false)} />
              <div className="absolute right-0 z-40 mt-2 w-48 rounded-xl border border-slate-200 bg-slate-100 p-1.5 shadow-lg animate-scale-in">
                <p className="px-2 py-1 text-xs font-medium text-slate-400">Layout</p>
                {LAYOUTS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => {
                      setView(value);
                      setLayoutMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-200"
                  >
                    <Check
                      className={clsx('h-4 w-4', view === value ? 'text-brand-600' : 'opacity-0')}
                    />
                    <span className="flex-1 text-left">{label}</span>
                    <Icon className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="relative mt-3 flex-1">
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand-500 bg-brand-50/80 text-brand-700 backdrop-blur-sm">
            <UploadCloud className="h-10 w-10" />
            <p className="text-sm font-medium">Drop files to upload here</p>
          </div>
        )}

        {isLoading ? (
          <div className="py-24">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 py-16 text-center text-sm text-red-600">
            {getErrorMessage(error, 'Failed to load this folder')}
          </div>
        ) : nothingToShow ? (
          <div className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 px-6 py-12 text-center animate-fade-in">
            <UploadCloud className="h-12 w-12 text-slate-400" />
            <p className="mt-4 text-base text-slate-500">
              {starredOnly ? 'No starred items yet' : 'Drop anything here to upload'}
            </p>
            {(canCreateFolders || canCreateFiles) && <div className="mt-10">
              <p className="mb-3 text-sm font-medium text-slate-600">Other ways to get started</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {canCreateFolders && <Button variant="secondary" onClick={() => setNewFolderOpen(true)}>
                  <FolderPlus className="h-4 w-4" />
                  Create a folder
                </Button>}
                {canCreateFiles && <UploadButton folderId={folderId} />}
              </div>
            </div>}
          </div>
        ) : view !== 'list' ? (
          <div
            className={clsx(
              'grid gap-3 animate-fade-in',
              view === 'large'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
            )}
          >
            {visFolders.map((folder) => (
              <div
                key={folder.id}
                onClick={() => handleOpen({ type: 'folder', item: folder })}
                onContextMenu={(e) => openItemMenu(e, 'folder', folder)}
                className={clsx(
                  'group relative flex min-w-0 aspect-square cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-center transition-all hover:border-slate-300 hover:shadow-md',
                  view === 'large' ? 'p-6' : 'p-4',
                )}
              >
                <button
                  type="button"
                  aria-label={`Actions for ${folder.name}`}
                  onClick={(e) => openItemMenu(e, 'folder', folder)}
                  className="absolute right-2 top-2 rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-800"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
                <Folder className={clsx('shrink-0 text-brand-600', view === 'large' ? 'h-20 w-20' : 'h-12 w-12')} />
                <span className="w-full truncate text-sm font-medium text-slate-800" title={folder.name}>{folder.name}</span>
                <span className="text-xs text-slate-400">Folder</span>
              </div>
            ))}
            {visFiles.map((file) => (
              <div
                key={file.id}
                onClick={() => handleOpen({ type: 'file', item: file })}
                onContextMenu={(e) => openItemMenu(e, 'file', file)}
                className={clsx(
                  'group relative flex min-w-0 aspect-square cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-center transition-all hover:border-slate-300 hover:shadow-md',
                  view === 'large' ? 'p-6' : 'p-4',
                )}
              >
                <button
                  type="button"
                  aria-label={`Actions for ${file.name}`}
                  onClick={(e) => openItemMenu(e, 'file', file)}
                  className="absolute right-2 top-2 rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-800"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
                {isImageFile(file) ? (
                  <ImageThumb
                    file={file}
                    className="min-h-0 w-full flex-1 rounded-lg border border-slate-200 object-cover"
                    fallback={
                      <FileText
                        className={clsx(
                          'shrink-0 text-slate-400',
                          view === 'large' ? 'h-12 w-12' : 'h-8 w-8',
                        )}
                      />
                    }
                  />
                ) : (
                  <FileText className={clsx('shrink-0 text-slate-400', view === 'large' ? 'h-12 w-12' : 'h-8 w-8')} />
                )}
                <span className="w-full truncate text-sm font-medium text-slate-800" title={file.name}>{file.name}</span>
                <span className="text-xs text-slate-400">{formatBytes(file.sizeBytes)}</span>
                <FileAvailability file={file} />
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 animate-fade-in">
            <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-400">
              <div className="w-4" />
              <div className="flex-1">Name</div>
              <div className="hidden w-24 text-right sm:block">Size</div>
              <div className="hidden w-40 text-right md:block">Modified</div>
              <div className="w-[160px]" />
            </div>
            {visFolders.map((folder) => (
              <ItemRow
                key={folder.id}
                type="folder"
                item={folder}
                onRename={canManageFolders ? setRenameTarget : undefined}
                onShare={canShare ? setShareTarget : undefined}
                onTrash={canManageFolders ? trashMutation.mutate : undefined}
                onComments={canComment ? setCommentTarget : undefined}
                onChat={canChat ? setChatTarget : undefined}
                onMove={canManageFolders ? setMoveTarget : undefined}
                onToggleFavorite={favoriteMutation.mutate}
                isFavorite={isFavorite('folder', folder.id)}
                commentCount={folderCounts[folder.id]?.total ?? 0}
                unresolvedCount={folderCounts[folder.id]?.unresolved ?? 0}
                chatTotal={folderChat[folder.id]?.total ?? 0}
                chatUnread={folderChat[folder.id]?.unread ?? 0}
                onOpen={handleOpen}
                onDownload={() => {}}
                onContextMenu={openItemMenu}
              />
            ))}
            {visFiles.map((file) => (
              <ItemRow
                key={file.id}
                type="file"
                item={file}
                onRename={canWriteFiles ? setRenameTarget : undefined}
                onShare={canShare ? setShareTarget : undefined}
                onTrash={canDeleteFiles ? trashMutation.mutate : undefined}
                onDownload={canDownload ? handleDownload : undefined}
                onOpen={handleOpen}
                onEdit={canWriteFiles ? handleEdit : undefined}
                // Version history hidden for now:
                // onVersions={setVersionTarget}
                onComments={canComment ? setCommentTarget : undefined}
                onChat={canChat ? setChatTarget : undefined}
                onMove={canWriteFiles ? setMoveTarget : undefined}
                onMetadata={canWriteFiles ? setMetaTarget : undefined}
                onToggleFavorite={favoriteMutation.mutate}
                isFavorite={isFavorite('file', file.id)}
                commentCount={fileCounts[file.id]?.total ?? 0}
                unresolvedCount={fileCounts[file.id]?.unresolved ?? 0}
                chatTotal={fileChat[file.id]?.total ?? 0}
                chatUnread={fileChat[file.id]?.unread ?? 0}
                onContextMenu={openItemMenu}
              />
            ))}
          </div>
        )}
      </div>

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            Page {meta.page} of {meta.totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={!meta.hasPreviousPage}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!meta.hasNextPage}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <NewFolderModal open={newFolderOpen} onClose={() => setNewFolderOpen(false)} parentId={folderId} />
      <RenameModal open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} target={renameTarget} />
      <ShareModal open={Boolean(shareTarget)} onClose={() => setShareTarget(null)} target={shareTarget} />
      {/* Version history hidden for now:
      <VersionHistoryModal
        open={Boolean(versionTarget)}
        onClose={() => setVersionTarget(null)}
        target={versionTarget}
      /> */}
      <MoveToFolderModal open={Boolean(moveTarget)} onClose={() => setMoveTarget(null)} target={moveTarget} />
      <MetadataModal
        open={Boolean(metaTarget)}
        onClose={() => setMetaTarget(null)}
        target={metaTarget}
        // Already fetched for the row badges — reuse it rather than refetch.
        commentCount={metaTarget ? (fileCounts[metaTarget.item.id]?.total ?? 0) : 0}
      />
      <CommentsModal open={Boolean(commentTarget)} onClose={() => setCommentTarget(null)} target={commentTarget} />
      <ChatModal open={Boolean(chatTarget)} onClose={closeChat} target={chatTarget} />

      {/* Right-click menu + its "Upload files" picker */}
      <input
        ref={uploadInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={async (e) => {
          await uploadMany(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={menuItems(ctxMenu)}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}
